import { emptyEFormSchema, isEFormSchema, type EFormSchema } from './schema';

export const EFORM_PACKAGE_FORMAT = 'atlas-eform';
export const EFORM_PACKAGE_FORMAT_VERSION = 1;

export type EFormPackageDefinition = {
  name?: string;
  code?: string;
  description?: string;
  isActive?: boolean;
  schema: EFormSchema;
};

export type EFormPackage = {
  format: typeof EFORM_PACKAGE_FORMAT;
  formatVersion: number;
  exportedAt: string;
  definition: EFormPackageDefinition;
};

export type ParsedEFormImport = {
  name?: string;
  code?: string;
  description?: string;
  isActive?: boolean;
  schema: EFormSchema;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function pickSchema(value: unknown): EFormSchema | null {
  if (isEFormSchema(value)) {
    const empty = emptyEFormSchema();
    return {
      ...empty,
      ...value,
      instructions: value.instructions ?? empty.instructions ?? '',
      settings: { ...empty.settings, ...(value.settings ?? {}) },
      sections: value.sections?.length ? value.sections : empty.sections,
    };
  }
  return null;
}

/** Build a portable package for download / exchange with other apps. */
export function buildEFormPackage(input: {
  name: string;
  code: string;
  description?: string;
  isActive?: boolean;
  schema: EFormSchema;
}): EFormPackage {
  return {
    format: EFORM_PACKAGE_FORMAT,
    formatVersion: EFORM_PACKAGE_FORMAT_VERSION,
    exportedAt: new Date().toISOString(),
    definition: {
      name: input.name,
      code: input.code,
      description: input.description ?? '',
      isActive: input.isActive !== false,
      schema: input.schema,
    },
  };
}

/**
 * Accepts:
 * - atlas-eform package `{ format, definition: { schema, … } }`
 * - `{ definition: { schema } }` / `{ schema }`
 * - raw `EFormSchema` (`{ sections: […] }`)
 */
export function parseEFormImportJson(raw: unknown): ParsedEFormImport {
  const root = asRecord(raw);
  if (!root) throw new Error('JSON must be an object.');

  const definition = asRecord(root.definition);
  const fromDefinitionSchema = definition ? pickSchema(definition.schema ?? definition.schemaJson) : null;
  const fromRootSchema = pickSchema(root.schema ?? root.schemaJson);
  const asSchema = pickSchema(raw);

  const schema = fromDefinitionSchema ?? fromRootSchema ?? asSchema;
  if (!schema) {
    throw new Error('Could not find a form schema (expected sections[]).');
  }

  const meta = definition ?? root;
  return {
    name: typeof meta.name === 'string' ? meta.name : undefined,
    code: typeof meta.code === 'string' ? meta.code : undefined,
    description: typeof meta.description === 'string' ? meta.description : undefined,
    isActive: typeof meta.isActive === 'boolean' ? meta.isActive : undefined,
    schema,
  };
}

export function downloadJsonFile(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function safeDownloadBasename(name: string, code: string): string {
  const base = (code || name || 'e-form').trim() || 'e-form';
  return base.replace(/[/\\?%*:|"<>]/g, '_').replace(/\s+/g, '-').slice(0, 80);
}
